import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { captureLeadSource } from "@/lib/leadSource";
import { HUMAN_VERIFIED_EVENT, isBotTraffic, isHumanVerified, startHumanVerification } from "@/lib/botDetection";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

const GA_ID = "G-DGDTXT309M";
let gaLoaded = false;
let pixelLoaded = false;
let tiktokLoaded = false;

/**
 * Carrega o gtag.js imediatamente (exceto bots evidentes) para que o Google
 * consiga detectar a tag no site. O page_view continua sendo enviado apenas
 * depois da verificacao humana (ver PageViewTracker).
 */
function loadGoogleAnalytics(): void {
  if (gaLoaded || isBotTraffic()) return;
  gaLoaded = true;

  window.dataLayer = window.dataLayer || [];
  // IMPORTANTE: o gtag.js exige o objeto `arguments` (nao um array) no dataLayer.
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  }
  window.gtag = gtag as unknown as (...args: unknown[]) => void;
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { send_page_view: true });
  const ga = document.createElement("script");
  ga.async = true;
  ga.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(ga);
}

function loadMetaPixelAfterHumanVerification(): void {
  if (pixelLoaded || !isHumanVerified()) return;
  pixelLoaded = true;

  const fbq = ((...args: unknown[]) => {
    const queue = (fbq as unknown as { queue: unknown[][] }).queue;
    queue.push(args);
  }) as typeof window.fbq & { queue: unknown[][]; loaded: boolean; version: string };
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = "2.0";
  window.fbq = fbq;
  window.fbq("init", "4014908828757463");
  window.fbq("track", "PageView");
  const meta = document.createElement("script");
  meta.async = true;
  meta.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(meta);
}

/**
 * Carrega o TikTok Pixel somente após a verificação humana, para não
 * registrar visitas de robôs nos relatórios do TikTok Ads.
 */
function loadTikTokPixelAfterHumanVerification(): void {
  if (tiktokLoaded || !isHumanVerified()) return;
  tiktokLoaded = true;

  const w = window as unknown as { TiktokAnalyticsObject?: string; ttq?: any };
  const t = "ttq";
  w.TiktokAnalyticsObject = t;
  const ttq: any = (w as any)[t] = (w as any)[t] || [];
  ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
  ttq.setAndDefer = function (target: any, method: string) {
    target[method] = function () {
      // eslint-disable-next-line prefer-rest-params
      target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
    };
  };
  for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
  ttq.instance = function (id: string) {
    const inst = ttq._i[id] || [];
    for (let n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(inst, ttq.methods[n]);
    return inst;
  };
  ttq.load = function (id: string, config?: { partner?: string }) {
    const url = "https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i = ttq._i || {};
    ttq._i[id] = [];
    ttq._i[id]._u = url;
    ttq._t = ttq._t || {};
    ttq._t[id] = +new Date();
    ttq._o = ttq._o || {};
    ttq._o[id] = config || {};
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = url + "?sdkid=" + id + "&lib=" + t;
    const first = document.getElementsByTagName("script")[0];
    first.parentNode?.insertBefore(script, first);
  };

  ttq.load("DAI2G8RC77UES974ULDG");
  ttq.page();
}

// Captura a origem (utm/gclid/publico) ANTES do primeiro render, para que o
// link do WhatsApp já saia com o rastreamento certo já na página de pouso do anúncio.
captureLeadSource();
loadGoogleAnalytics();
window.addEventListener(HUMAN_VERIFIED_EVENT, loadMetaPixelAfterHumanVerification, { once: true });
window.addEventListener(HUMAN_VERIFIED_EVENT, loadTikTokPixelAfterHumanVerification, { once: true });
startHumanVerification();
loadMetaPixelAfterHumanVerification();
loadTikTokPixelAfterHumanVerification();


createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>
);
